/**
 * The cards linking to microbit.org and the support site from the home page.
 *
 * (c) 2026, Micro:bit Educational Foundation and contributors
 *
 * SPDX-License-Identifier: MIT
 */
import { IntlShape } from "react-intl";
import accessibilityImage from "theme-package/images/accessibility.svg";
import firstLessonsImage from "theme-package/images/first-lessons-python.svg";
import ideaEmotionBadge from "theme-package/images/idea-emotion-badge.png";
import ideaStepCounter from "theme-package/images/idea-step-counter.png";
import ideaActivityPicker from "theme-package/images/idea-activity-picker.png";
import ideaSendASmile from "theme-package/images/idea-send-a-smile.png";
import ideaDice from "theme-package/images/idea-dice.png";
import ideaClapLights from "theme-package/images/idea-clap-lights.png";
import ideaFrereJacques from "theme-package/images/idea-frere-jacques.png";
import hotPotatoGame from "theme-package/images/hot-potato-game.jpg";
import indoorOutdoorThermometer from "theme-package/images/indoor-outdoor-thermometer.png";
import introducingPythonCourseImage from "theme-package/images/introducing-python-course.png";
import labellingWorksheetImage from "theme-package/images/microbit-labelling-worksheet.jpg";
import microbitClassroomImage from "theme-package/images/microbit-classroom.png";
import posterCreativityImage from "theme-package/images/python-editor-poster-creativity.jpg";
import posterDebuggingImage from "theme-package/images/python-editor-poster-debugging.jpg";
import maxminTemperatureLogger from "theme-package/images/maxmin-temperature-logger.png";
import sensitiveStepCounter from "theme-package/images/sensitive-step-counter.jpg";
import teleportingDuck from "theme-package/images/teleporting-duck.jpg";
import thermometer from "theme-package/images/thermometer.png";
import treasureHunt from "theme-package/images/treasure-hunt.png";
import troubleshootingImage from "theme-package/images/troubleshooting.svg";
import userGuideImage from "theme-package/images/user-guide.svg";
import { BrandConfig } from "../deployment";
import {
  introducingPythonCourseUrl,
  microbitClassroomUrl,
  microbitOrgClassroomResourceUrl,
  microbitOrgLessonUrl,
  microbitOrgProjectUrl,
} from "../external-links";
import { Level } from "./LevelIndicator";
import ResourceCard from "./ResourceCard";

interface Idea {
  titleId: string;
  /** The idea's slug in the CMS ideas. */
  slug: string;
  imgSrc: string;
}

const ideas: Idea[] = [
  {
    titleId: "idea-emotion-badge-title",
    slug: "emotion-badge",
    imgSrc: ideaEmotionBadge,
  },
  {
    titleId: "idea-step-counter-title",
    slug: "step-counter",
    imgSrc: ideaStepCounter,
  },
  {
    titleId: "idea-activity-picker-title",
    slug: "activity-picker",
    imgSrc: ideaActivityPicker,
  },
  {
    titleId: "idea-send-a-smile-title",
    slug: "send-a-smile",
    imgSrc: ideaSendASmile,
  },
  { titleId: "idea-dice-title", slug: "dice", imgSrc: ideaDice },
  {
    titleId: "idea-clap-lights-title",
    slug: "clap-lights",
    imgSrc: ideaClapLights,
  },
  {
    titleId: "idea-frere-jacques-title",
    slug: "frère-jacques",
    imgSrc: ideaFrereJacques,
  },
];

/** Idea cards show the idea, by slug, for the user to open or not. */
export const createIdeaCards = (
  intl: IntlShape,
  onSelect: (slug: string, title: string) => void
) =>
  ideas.map((idea) => (
    <ResourceCard
      key={idea.titleId}
      title={intl.formatMessage({ id: idea.titleId })}
      onClick={() =>
        onSelect(idea.slug, intl.formatMessage({ id: idea.titleId }))
      }
      imgSrc={idea.imgSrc}
      // The ideas tab's images are 2:1; show them whole.
      wide
    />
  ));

interface MiciProject {
  titleId: string;
  descriptionId: string;
  level: Level;
  /** The "make it: code it" project's slug on microbit.org. */
  slug: string;
  imgSrc: string;
}

const miciProjects: MiciProject[] = [
  {
    titleId: "mici-project-thermometer-title",
    descriptionId: "mici-project-thermometer-description",
    level: "beginner",
    slug: "thermometer",
    imgSrc: thermometer,
  },
  {
    titleId: "mici-project-sensitive-step-counter-title",
    descriptionId: "mici-project-sensitive-step-counter-description",
    level: "beginner",
    slug: "sensitive-step-counter",
    imgSrc: sensitiveStepCounter,
  },
  {
    titleId: "mici-project-treasure-hunt-title",
    descriptionId: "mici-project-treasure-hunt-description",
    level: "intermediate",
    slug: "treasure-hunt",
    imgSrc: treasureHunt,
  },
  {
    titleId: "mici-project-maxmin-temperature-logger-title",
    descriptionId: "mici-project-maxmin-temperature-logger-description",
    level: "advanced",
    slug: "maxmin-temperature-logger",
    imgSrc: maxminTemperatureLogger,
  },
  {
    titleId: "mici-project-indoor-outdoor-thermometer-title",
    descriptionId: "mici-project-indoor-outdoor-thermometer-description",
    level: "intermediate",
    slug: "indoor-outdoor-thermometer",
    imgSrc: indoorOutdoorThermometer,
  },
  {
    titleId: "mici-project-teleporting-duck-title",
    descriptionId: "mici-project-teleporting-duck-description",
    level: "beginner",
    slug: "teleporting-duck",
    imgSrc: teleportingDuck,
  },
  {
    titleId: "mici-project-hot-potato-game-title",
    descriptionId: "mici-project-hot-potato-game-description",
    level: "intermediate",
    slug: "hot-potato-game",
    imgSrc: hotPotatoGame,
  },
];

export const createMiciProjectCards = (intl: IntlShape, languageId: string) =>
  miciProjects.map((project) => (
    <ResourceCard
      key={project.titleId}
      title={intl.formatMessage({ id: project.titleId })}
      description={intl.formatMessage({ id: project.descriptionId })}
      level={project.level}
      url={microbitOrgProjectUrl(project.slug, languageId)}
      imgSrc={project.imgSrc}
    />
  ));

interface TeacherResource {
  /** Message id prefix for `-title` and `-description`. */
  id: string;
  url: string;
  imgSrc: string;
}

const teacherResources: TeacherResource[] = [
  {
    id: "introducing-python-course-resource",
    url: introducingPythonCourseUrl,
    imgSrc: introducingPythonCourseImage,
  },
  {
    id: "microbit-classroom-resource",
    url: microbitClassroomUrl,
    imgSrc: microbitClassroomImage,
  },
  {
    id: "python-editor-poster-creativity-resource",
    url: microbitOrgClassroomResourceUrl("python-editor-poster-creativity"),
    imgSrc: posterCreativityImage,
  },
  {
    id: "python-editor-poster-debugging-resource",
    url: microbitOrgClassroomResourceUrl("python-editor-poster-debugging"),
    imgSrc: posterDebuggingImage,
  },
  {
    id: "microbit-labelling-worksheet-resource",
    url: microbitOrgClassroomResourceUrl("microbit-labelling-worksheet"),
    imgSrc: labellingWorksheetImage,
  },
];

export const createLessonCards = (intl: IntlShape) => [
  <ResourceCard
    key="first-lessons"
    title={intl.formatMessage({
      id: "first-lessons-with-python-resource-title",
    })}
    description={intl.formatMessage({
      id: "first-lessons-with-python-resource-description",
    })}
    url={microbitOrgLessonUrl("first-lessons-with-python-and-the-microbit")}
    imgSrc={firstLessonsImage}
    imageFit="contain"
    imagePadding={5}
  />,
  ...teacherResources.map((resource) => (
    <ResourceCard
      key={resource.id}
      title={intl.formatMessage({ id: `${resource.id}-title` })}
      description={intl.formatMessage({ id: `${resource.id}-description` })}
      url={resource.url}
      imgSrc={resource.imgSrc}
    />
  )),
];

type HelpLinks = Pick<
  BrandConfig,
  "userGuideLink" | "supportLink" | "accessibilityLink"
>;

/**
 * Help cards for the links the brand supplies; a deployment without one
 * has no card for it.
 */
export const createHelpCards = (
  intl: IntlShape,
  { userGuideLink, supportLink, accessibilityLink }: HelpLinks
) =>
  [
    { titleId: "user-guide", url: userGuideLink, imgSrc: userGuideImage },
    {
      titleId: "troubleshooting-resource-title",
      url: supportLink,
      imgSrc: troubleshootingImage,
    },
    {
      titleId: "accessibility-resource-title",
      url: accessibilityLink,
      imgSrc: accessibilityImage,
    },
  ]
    .filter((help): help is typeof help & { url: string } => !!help.url)
    .map((help) => (
      <ResourceCard
        key={help.titleId}
        title={intl.formatMessage({ id: help.titleId })}
        url={help.url}
        imgSrc={help.imgSrc}
        imageFit="contain"
      />
    ));
